import { LngLatBounds, Map as MapLibreMap, setWorkerUrl } from 'maplibre-gl';
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import type { TimelineCoordinate, TimelinePoint } from './timeline';
import { videoMapSafeFrame } from './video-layout';

setWorkerUrl(maplibreWorkerUrl);

export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

export interface GeographicMarker {
  readonly coordinate: TimelineCoordinate;
  readonly kind: 'trip';
  readonly label: string;
}

export interface ScreenMarker extends ScreenPoint {
  readonly kind: GeographicMarker['kind'];
  readonly label: string;
}

export interface MapSnapshot {
  readonly background: HTMLCanvasElement;
  readonly route: ScreenPoint[];
  readonly markers: ScreenMarker[];
}

const MAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';
const MAP_RENDER_SCALE = 1.5;

function abortError(): DOMException {
  return new DOMException('영상 만들기가 취소됐습니다.', 'AbortError');
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw abortError();
}

function longitudeWindow(points: readonly TimelinePoint[]): { values: number[]; min: number; max: number } {
  const normalized = points.map((point) => ((point.longitude % 360) + 360) % 360);
  const sorted = [...new Set(normalized)].sort((a, b) => a - b);
  if (sorted.length <= 1) {
    const value = sorted[0] ?? 0;
    return { values: normalized, min: value, max: value };
  }

  let largestGap = -1;
  let windowStart = sorted[0];
  for (let index = 0; index < sorted.length; index += 1) {
    const current = sorted[index];
    const next = index === sorted.length - 1 ? sorted[0] + 360 : sorted[index + 1];
    const gap = next - current;
    if (gap > largestGap) {
      largestGap = gap;
      windowStart = next % 360;
    }
  }
  const values = normalized.map((longitude) => longitude < windowStart ? longitude + 360 : longitude);
  return { values, min: Math.min(...values), max: Math.max(...values) };
}

function unwrapLongitude(longitude: number, center: number): number {
  let value = ((longitude % 360) + 360) % 360;
  while (value - center > 180) value -= 360;
  while (center - value > 180) value += 360;
  return value;
}

function waitForRender(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => finish(), milliseconds);
    const onAbort = (): void => finish(abortError());
    const finish = (error?: Error): void => {
      window.clearTimeout(timeout);
      signal?.removeEventListener('abort', onAbort);
      if (error) reject(error);
      else resolve();
    };
    if (signal?.aborted) {
      finish(abortError());
      return;
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function nextPaint(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}

async function waitForDetailedMap(map: MapLibreMap, signal?: AbortSignal): Promise<void> {
  const deadline = Date.now() + 12_000;
  let readySince: number | null = null;
  let styleWasReady = false;
  let sourcesWereReady = false;
  let detailedFeaturesWereReady = false;
  while (Date.now() < deadline) {
    throwIfAborted(signal);
    map.triggerRepaint();
    await nextPaint();
    try {
      const style = map.getStyle();
      const sourceIds = Object.keys(style.sources ?? {});
      styleWasReady ||= (style.layers?.length ?? 0) > 0 && sourceIds.length > 0;
      sourcesWereReady = sourceIds.length > 0
        && sourceIds.every((sourceId) => map.isSourceLoaded(sourceId));
      const renderedFeatures = map.queryRenderedFeatures();
      detailedFeaturesWereReady = renderedFeatures.some((feature) => (
        feature.layer.type === 'fill'
        || feature.layer.type === 'line'
        || feature.layer.type === 'symbol'
      ));

      if (styleWasReady && sourcesWereReady && detailedFeaturesWereReady) {
        readySince ??= Date.now();
        // Source completion happens just before the final WebGL paint. Keep
        // the surface alive for a few more frames so raster terrain and road
        // layers are present when it is copied into the video canvas.
        if (Date.now() - readySince >= 750) return;
      } else {
        readySince = null;
      }
    } catch {
      // The style and its sources can briefly be unavailable while MapLibre
      // builds the first render tree.
    }
    await waitForRender(250, signal);
  }
  throw new Error('상세 지도와 도로 정보를 불러오지 못했어요. 네트워크를 확인하고 다시 시도해 주세요.');
}

export async function createMapSnapshot(
  points: readonly TimelinePoint[],
  width: number,
  height: number,
  markers: readonly GeographicMarker[] = [],
  signal?: AbortSignal,
): Promise<MapSnapshot> {
  throwIfAborted(signal);
  const renderWidth = Math.round(width * MAP_RENDER_SCALE);
  const renderHeight = Math.round(height * MAP_RENDER_SCALE);
  const container = document.createElement('div');
  container.className = 'map-render-surface';
  container.style.width = `${renderWidth}px`;
  container.style.height = `${renderHeight}px`;
  document.body.append(container);

  let map: MapLibreMap | null = null;
  try {
    const longitude = longitudeWindow(points);
    const latitudes = points.map((point) => point.latitude);
    let minLatitude = Math.min(...latitudes);
    let maxLatitude = Math.max(...latitudes);
    let minLongitude = longitude.min;
    let maxLongitude = longitude.max;
    if (maxLatitude - minLatitude < 0.005) {
      minLatitude -= 0.004;
      maxLatitude += 0.004;
    }
    if (maxLongitude - minLongitude < 0.005) {
      minLongitude -= 0.004;
      maxLongitude += 0.004;
    }

    const renderedMap = new MapLibreMap({
      container,
      style: MAP_STYLE_URL,
      center: [(minLongitude + maxLongitude) / 2, (minLatitude + maxLatitude) / 2],
      zoom: 2,
      interactive: false,
      attributionControl: false,
      fadeDuration: 0,
      canvasContextAttributes: { preserveDrawingBuffer: true },
      pixelRatio: 1,
    });
    map = renderedMap;
    const safeFrame = videoMapSafeFrame(width, height);
    renderedMap.fitBounds(new LngLatBounds(
      [minLongitude, minLatitude],
      [maxLongitude, maxLatitude],
    ), {
      padding: {
        left: Math.round(safeFrame.left * MAP_RENDER_SCALE),
        right: Math.round((width - safeFrame.right) * MAP_RENDER_SCALE),
        top: Math.round(safeFrame.top * MAP_RENDER_SCALE),
        bottom: Math.round((height - safeFrame.bottom) * MAP_RENDER_SCALE),
      },
      animate: false,
      maxZoom: 13,
    });

    await waitForDetailedMap(renderedMap, signal);
    throwIfAborted(signal);
    renderedMap.triggerRepaint();
    await nextPaint();

    const background = document.createElement('canvas');
    background.width = renderWidth;
    background.height = renderHeight;
    const context = background.getContext('2d');
    if (!context) throw new Error('브라우저에서 지도를 그릴 수 없습니다.');
    context.drawImage(renderedMap.getCanvas(), 0, 0, renderWidth, renderHeight);

    const route = points.map((point, index) => {
      const projected = renderedMap.project([longitude.values[index], point.latitude]);
      return { x: projected.x / MAP_RENDER_SCALE, y: projected.y / MAP_RENDER_SCALE };
    });
    const longitudeCenter = (minLongitude + maxLongitude) / 2;
    const screenMarkers = markers.map((marker) => {
      const projected = renderedMap.project([
        unwrapLongitude(marker.coordinate.longitude, longitudeCenter),
        marker.coordinate.latitude,
      ]);
      return {
        x: projected.x / MAP_RENDER_SCALE,
        y: projected.y / MAP_RENDER_SCALE,
        kind: marker.kind,
        label: marker.label,
      };
    });
    return { background, route, markers: screenMarkers };
  } finally {
    map?.remove();
    container.remove();
  }
}
